import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PushNotificationButtonProps {
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showLabel?: boolean;
  className?: string;
}

/**
 * Button component for subscribing to push notifications
 * Shows different states: unsupported, unsubscribed, subscribed, loading
 */
export function PushNotificationButton({ 
  variant = 'outline',
  size = 'sm',
  showLabel = true,
  className = ''
}: PushNotificationButtonProps) {
  const { isSupported, isSubscribed, isLoading, subscribe, unsubscribe } = usePushNotifications();

  // Don't render if not supported
  if (!isSupported) {
    return null;
  }

  const handleClick = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  const buttonContent = isLoading ? (
    <>
      <Loader2 className="w-4 h-4 animate-spin" />
      {showLabel && <span className="ml-2">Loading...</span>}
    </>
  ) : isSubscribed ? (
    <>
      <BellRing className="w-4 h-4 text-primary" />
      {showLabel && <span className="ml-2">Alerts On</span>}
    </>
  ) : (
    <>
      <Bell className="w-4 h-4" />
      {showLabel && <span className="ml-2">Get Alerts</span>}
    </>
  );

  const tooltipText = isSubscribed 
    ? 'Click to disable push notifications' 
    : 'Get notified when breaking crypto news drops';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={isSubscribed ? 'default' : variant}
            size={size}
            onClick={handleClick}
            disabled={isLoading}
            className={className}
          >
            {buttonContent}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
